import React from "react";
import Pagination from "rc-pagination";
import {Button, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import {locale} from "../../common/table/paginatorConfig";
import {SortIcons} from "components/SortIcons/SortIcons";
import {IPageResponse, SortDirection} from "common/interfaces/IPageResponse";
import {dateFormatOptions} from "common/dateFormatterConfig";
import {
    GHG_EMISSION_COLUMNS,
    GhgEmissionPeriod,
    GhgEmissionPeriodForm,
    GhgEmissionPeriodSortFields
} from "./models";
import {GhgEmissionPeriodEditableRow} from "./GhgEmissionPeriodEditableRow";

interface GhgEmissionPeriodsTableProps {
    periods?: IPageResponse<GhgEmissionPeriod>;
    onPageChange: (page: number, size: number) => void;
    onSort: (sortField: GhgEmissionPeriodSortFields) => void;
    sortField: GhgEmissionPeriodSortFields;
    sortDirection: SortDirection;
    isRequesting: boolean;
    createForm?: GhgEmissionPeriodForm;
    editForm?: GhgEmissionPeriodForm;
    editId?: number;
    validated: boolean;
    saving: boolean;
    onFormChange: (change: Partial<GhgEmissionPeriodForm>) => void;
    onSave: () => void;
    onCancel: () => void;
    onStartEdit: (period: GhgEmissionPeriod) => void;
    onDelete: (period: GhgEmissionPeriod) => void;
}

function formatYearMonth(value?: string) {
    if (!value) return "";
    const [year, month] = value.split("-");
    return month && year ? `${month}.${year}` : value;
}

export function GhgEmissionPeriodsTable(props: GhgEmissionPeriodsTableProps) {
    const periods: Array<GhgEmissionPeriod> = props.periods ? props.periods.content : [];
    const pageResponse = props.periods;
    const isRowBeingEdited = props.editId !== undefined || props.createForm !== undefined;
    const columnCount = GHG_EMISSION_COLUMNS.length + 4;

    const periodRows = periods.map((period) => {
        if (props.editId === period.id && props.editForm) {
            return (
                <GhgEmissionPeriodEditableRow
                    key={'row-' + period.id}
                    form={props.editForm}
                    period={period}
                    validated={props.validated}
                    saving={props.saving}
                    onChange={props.onFormChange}
                    onSave={props.onSave}
                    onCancel={props.onCancel}
                />
            );
        }

        return (
            <tr key={'row-' + period.id}>
                <td className="text-nowrap">
                    {formatYearMonth(period.periodStart)}
                </td>
                <td className="text-nowrap">
                    {formatYearMonth(period.periodEnd)}
                </td>
                {
                    GHG_EMISSION_COLUMNS.map(({key}) => (
                        <td key={key} className="text-nowrap">
                            {period[key as keyof GhgEmissionPeriod] as number}
                        </td>
                    ))
                }
                <td className="text-nowrap">
                    {period.dateCreated ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(period.dateCreated)) : ""}
                </td>
                <td>
                    <div className="row__actions">
                        <OverlayTrigger
                            delay={250}
                            overlay={
                                <Tooltip id={`tooltip-edit-${period.id}`}>
                                    <p style={{margin: '3px'}}>Muuda</p>
                                </Tooltip>
                            }
                            placement={"left"}
                        >
                            <Button
                                size="sm"
                                variant="outline-dark"
                                className="d-flex align-items-center border-color-background border-radius-circle btn-outline-icon"
                                disabled={isRowBeingEdited}
                                onClick={() => props.onStartEdit(period)}
                            >
                                <i className="icon--16 my-2 icon__edit--outline-mask"/>
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                            delay={250}
                            overlay={
                                <Tooltip id={`tooltip-delete-${period.id}`}>
                                    <p style={{margin: '3px'}}>Kustuta</p>
                                </Tooltip>
                            }
                            placement={"left"}
                        >
                            <Button
                                size="sm"
                                variant="outline-danger"
                                className="d-flex align-items-center border-color-background border-radius-circle btn-outline-icon"
                                disabled={isRowBeingEdited}
                                onClick={() => props.onDelete(period)}
                            >
                                <i className="icon--16 my-2 icon__delete--outline-mask"/>
                            </Button>
                        </OverlayTrigger>
                    </div>
                </td>
            </tr>
        );
    });

    return (
        <div className={"ghg-emission-periods__table-wrapper" + (isRowBeingEdited ? " ghg-emission-periods__table-wrapper--row-open" : "")}>
            <table className='generic-table table table-hover ghg-emission-periods__table'>
                <colgroup>
                    <col className="ghg-col--period"/>
                    <col className="ghg-col--period"/>
                    {
                        GHG_EMISSION_COLUMNS.map(({key}) => (
                            <col key={key} className="ghg-col--emission"/>
                        ))
                    }
                    <col className="ghg-col--created"/>
                    <col className="ghg-col--actions"/>
                </colgroup>
                <thead>
                <tr>
                    <th className="sort-down-up-hover">
                        <div className="d-flex align-items-center"
                             onClick={() => props.onSort(GhgEmissionPeriodSortFields.PERIOD_START)}>
                            <span>Perioodi algus</span>
                            <SortIcons
                                sortedByField={props.sortField}
                                sortDirection={props.sortDirection}
                                currentSortColumn={GhgEmissionPeriodSortFields.PERIOD_START}/>
                        </div>
                    </th>
                    <th className="sort-down-up-hover">
                        <div className="d-flex align-items-center"
                             onClick={() => props.onSort(GhgEmissionPeriodSortFields.PERIOD_END)}>
                            <span>Perioodi lõpp</span>
                            <SortIcons
                                sortedByField={props.sortField}
                                sortDirection={props.sortDirection}
                                currentSortColumn={GhgEmissionPeriodSortFields.PERIOD_END}/>
                        </div>
                    </th>
                    {
                        GHG_EMISSION_COLUMNS.map(({key, label}) => (
                            <th key={key}>{label}</th>
                        ))
                    }
                    <th className="sort-down-up-hover">
                        <div className="d-flex align-items-center"
                             onClick={() => props.onSort(GhgEmissionPeriodSortFields.DATE_CREATED)}>
                            <span>Loodud</span>
                            <SortIcons
                                sortedByField={props.sortField}
                                sortDirection={props.sortDirection}
                                currentSortColumn={GhgEmissionPeriodSortFields.DATE_CREATED}/>
                        </div>
                    </th>
                </tr>
                </thead>
                <tbody>
                {
                    props.isRequesting ? (
                        <tr>
                            <td colSpan={columnCount} className="spinner-cell">
                                <div className="p-4 w-100 d-flex align-items-center justify-content-center">
                                    <Spinner animation="grow"/>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        <>
                            {
                                props.createForm && (
                                    <GhgEmissionPeriodEditableRow
                                        form={props.createForm}
                                        validated={props.validated}
                                        saving={props.saving}
                                        onChange={props.onFormChange}
                                        onSave={props.onSave}
                                        onCancel={props.onCancel}
                                    />
                                )
                            }
                            {periodRows}
                            {
                                !periodRows.length && !props.createForm && (
                                    <tr>
                                        <td colSpan={columnCount} className="text-center">
                                            Kirjeid ei leitud.
                                        </td>
                                    </tr>
                                )
                            }
                        </>
                    )
                }
                </tbody>
                {
                    pageResponse ? (
                        <tfoot>
                        <tr>
                            <td colSpan={columnCount}>
                                <div className="pagination-wrapper">
                                    <Pagination
                                        locale={locale}
                                        current={pageResponse.number + 1}
                                        total={pageResponse.totalElements}
                                        pageSize={pageResponse.size}
                                        prefixCls={"pagination"}
                                        onChange={props.onPageChange}
                                        showSizeChanger={false}
                                        hideOnSinglePage={true}
                                    />
                                </div>
                            </td>
                        </tr>
                        </tfoot>
                    ) : null
                }
            </table>
        </div>
    );
}
